import { type CallSubStatusListItem, type CallSubStatusListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { listCallSubStatuses } from '../call-sub-statuses.api';
import { CallSubStatusFormDrawer } from '../components/CallSubStatusFormDrawer';
import {
  useBulkDeleteCallSubStatuses,
  useBulkSetCallSubStatusesStatus,
  useCallSubStatusesList,
  useDeleteCallSubStatus,
} from '../use-call-sub-statuses';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCallStatusesList } from '@/features/call-statuses/use-call-statuses';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

/// Parent call status master has no `/options` endpoint; we use the
/// regular list with a generous pageSize. ~10-20 parent rows in
/// practice, so one fetch is fine.
const PARENT_LIST_QUERY = {
  page: 1 as const,
  pageSize: 200 as const,
  sortDir: 'asc' as const,
  sortBy: 'sortOrder',
  isActive: true,
};

export const CallSubStatusesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCallSubStatus = useDeleteCallSubStatus();
  const bulkDeleteMut = useBulkDeleteCallSubStatuses();
  const bulkSetStatusMut = useBulkSetCallSubStatusesStatus();
  const parentQuery = useCallStatusesList(PARENT_LIST_QUERY);

  const parentStatusOptions: ReadonlyArray<{ value: string; label: string }> = useMemo(() => {
    const entries = (parentQuery.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }));
    return [{ value: '', label: 'All call statuses' }, ...entries];
  }, [parentQuery.data]);

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'parentStatus',
        label: 'Call status',
        options: parentStatusOptions,
        widthClass: 'w-56',
      },
      {
        id: 'status',
        label: 'Active',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
    ],
    [parentStatusOptions],
  );

  const columns: ListScreenColumns<CallSubStatusListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CallSubStatusListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'callStatus',
        header: 'Parent status',
        cell: (row) => <span className="text-zinc-700">{row.callStatus.name}</span>,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'followupReasons',
        header: 'Follow-up reasons',
        // Empty mapping is "unrestricted", not "none" - label it so the
        // operator doesn't read a blank cell as a broken dropdown. A
        // lead-closing row never shows the follow-up dropdown at all, so
        // it gets a dash (mirrors the Lost reasons column below).
        cell: (row) => {
          if (row.isLostReason) return <span className="text-xs text-zinc-400">-</span>;
          const reasons = row.followupReasons ?? [];
          if (reasons.length === 0) {
            return <span className="text-xs text-zinc-400">All reasons</span>;
          }
          const shown = reasons.slice(0, 2);
          return (
            <div className="flex flex-wrap items-center gap-1">
              {shown.map((r) => (
                <UzBadge key={r.id} tone="secondary" pill>
                  {r.name}
                </UzBadge>
              ))}
              {reasons.length > shown.length && (
                <span
                  className="text-xs text-zinc-500"
                  title={reasons.map((r) => r.name).join(', ')}
                >
                  +{reasons.length - shown.length}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'lostReasons',
        header: 'Lost reasons',
        // Only meaningful for rows that close the lead; a dash reads
        // clearer than "All reasons" where no lost dropdown ever renders.
        cell: (row) => {
          if (!row.isLostReason) return <span className="text-xs text-zinc-400">-</span>;
          const reasons = row.lostReasons ?? [];
          if (reasons.length === 0) {
            return <span className="text-xs text-zinc-400">All lost reasons</span>;
          }
          const shown = reasons.slice(0, 2);
          return (
            <div className="flex flex-wrap items-center gap-1">
              {shown.map((r) => (
                <UzBadge key={r.id} tone="secondary" pill>
                  {r.name}
                </UzBadge>
              ))}
              {reasons.length > shown.length && (
                <span
                  className="text-xs text-zinc-500"
                  title={reasons.map((r) => r.name).join(', ')}
                >
                  +{reasons.length - shown.length}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CallSubStatusListItem, CallSubStatusListQuery>
      title="Call Sub Statuses"
      description="Finer breakdown of a call status (e.g. Connected → Reached and quoted)."
      useList={useCallSubStatusesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const parentStatus = state.filters.parentStatus;
        return {
          ...base,
          ...(parentStatus ? { callStatusId: parentStatus } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add call sub status' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CallSubStatusFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          callSubStatus={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} call sub status${rows.length === 1 ? '' : 'es'}?`,
            description:
              'These will be soft-deleted and hidden from lists. Follow-ups already recorded with them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} call sub status${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will appear in pickers again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCallSubStatuses(q),
        filename: 'call-sub-statuses',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Parent status', value: (r) => r.callStatus.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          {
            header: 'Follow-up reasons',
            value: (r) =>
              r.isLostReason
                ? ''
                : (r.followupReasons ?? []).length === 0
                  ? 'All reasons'
                  : (r.followupReasons ?? []).map((fr) => fr.name).join(', '),
          },
          {
            header: 'Lost reasons',
            value: (r) =>
              !r.isLostReason
                ? ''
                : (r.lostReasons ?? []).length === 0
                  ? 'All lost reasons'
                  : (r.lostReasons ?? []).map((lr) => lr.name).join(', '),
          },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCallSubStatus.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this call sub status?',
            description: `${row.name} will be soft-deleted and hidden from lists. Follow-ups already recorded with it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No call sub statuses match these filters',
        emptyDescription: 'Adjust the parent status or active filter, or clear the search box.',
        errorTitle: 'Failed to load call sub statuses',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
